import type {
  RuleBuilderRule,
  RuleBuilderGroup,
  RuleBuilderChild
} from '@/components/designSystem/RuleBuilder/types'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

const { flags } = useFeatureFlags()

export enum ValidationRole {
  OPERATION = 'OPERATION',
  MEDICAL_COORDINATOR = 'MEDICAL_COORDINATOR',
  AUDIT_ANALYST = 'AUDIT_ANALYST',
  TECHNICAL_VALIDATOR = 'TECHNICAL_VALIDATOR',
  STRATEGIC_VALIDATOR = 'STRATEGIC_VALIDATOR',
  EXECUTIVE_VALIDATOR = 'EXECUTIVE_VALIDATOR'
}

export const VALIDATION_ROLE_LABELS: Record<ValidationRole, string> = {
  [ValidationRole.OPERATION]: 'Operação',
  [ValidationRole.MEDICAL_COORDINATOR]: 'Tático',
  [ValidationRole.AUDIT_ANALYST]: 'Análise de Risco',
  [ValidationRole.TECHNICAL_VALIDATOR]: 'Técnica',
  [ValidationRole.STRATEGIC_VALIDATOR]: 'Estratégica',
  [ValidationRole.EXECUTIVE_VALIDATOR]: 'Executiva'
}

export const ALLOWED_VALIDATION_ROLES: ValidationRole[] = [
  ValidationRole.OPERATION,
  ValidationRole.MEDICAL_COORDINATOR,
  ValidationRole.AUDIT_ANALYST,
  ValidationRole.TECHNICAL_VALIDATOR,
  ValidationRole.STRATEGIC_VALIDATOR,
  ValidationRole.EXECUTIVE_VALIDATOR
]

/**
 * Papéis que todo fluxo de validação de excepcionalidade precisa ter no nível
 * raiz. São semeados na criação, reinjetados no carregamento e na importação, e
 * exibidos como "Perfil Padrão" (não removíveis) no card de camada. A ordem do
 * array é a ordem em que aparecem no topo do fluxo.
 *
 * Com `enabledMandatoryRiskValidator` desligada (default), só Operação é
 * obrigatória — Análise de Risco vira uma regra removível como outra qualquer.
 */
export const MANDATORY_VALIDATION_ROLES: readonly ValidationRole[] =
  flags.enabledMandatoryRiskValidator
    ? [ValidationRole.OPERATION, ValidationRole.AUDIT_ANALYST]
    : [ValidationRole.OPERATION]

/**
 * Com a flag ligada, o operador da raiz do fluxo fica travado em `AND`: os
 * obrigatórios valem como exigência conjunta. Desligada, a raiz fica livre
 * como qualquer outro grupo.
 */
export const ROOT_LAYER_REQUIRES_AND = flags.enabledMandatoryRiskValidator

export function isActiveValidationRole(value: unknown): value is ValidationRole {
  return Object.values(ValidationRole).includes(value as ValidationRole)
}

export enum LayerOperator {
  AND = 'AND',
  OR = 'OR'
}

export const LAYER_OPERATOR_LABELS: Record<LayerOperator, string> = {
  [LayerOperator.AND]: 'E',
  [LayerOperator.OR]: 'OU'
}

export type ApprovalRule = RuleBuilderRule<ValidationRole | null>

export type ApprovalGroup = RuleBuilderGroup<LayerOperator, ValidationRole | null>

export type ApprovalGroupChild = RuleBuilderChild<LayerOperator, ValidationRole | null>

export enum ApprovalInstanceStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED',
  CANCELED = 'CANCELED'
}

export enum ApprovalDecisionStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED'
}

export enum WebNotificationType {
  EXCEPTION_APPROVAL_REQUIRED = 'EXCEPTION_APPROVAL_REQUIRED',
  EXCEPTION_APPROVED = 'EXCEPTION_APPROVED',
  EXCEPTION_REJECTED = 'EXCEPTION_REJECTED',
  EXCEPTION_CANCELED = 'EXCEPTION_CANCELED',
  EXCEPTION_REVERTED = 'EXCEPTION_REVERTED',
  // Reversão multi-etapa: pedido de voto aos validadores e recusa para quem iniciou.
  EXCEPTION_REVERSAL_REQUIRED = 'EXCEPTION_REVERSAL_REQUIRED',
  EXCEPTION_REVERSAL_REJECTED = 'EXCEPTION_REVERSAL_REJECTED'
}
